import styled from "@emotion/styled";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShareNetwork, QrCode, Cards } from "@phosphor-icons/react";
import QRCode from "react-qr-code";

import { paper, fonts, shade } from "../theme";
import { joinUrl } from "../route";
import { getHostName } from "../players";
import { CastButton } from "../cast/CastButton";
import { useWebSocket } from "../useWebSocket";
import type { CommonRoomData, PlayerInfo, WaitingRoomView } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsEyebrow, MsRule,
    MsIndex, MsPaper, MsTape, MsBtn, MsIconBtn,
    MsRow, MsAvatar, MsName, MsNameMark, MsPlayerButton,
    MsSheet, MaskHeadline, MsWaitingFor,
    Stack, Row, MsSplitGrid,
} from "../ms";

/* Room-code index card: centred, with headroom above the punch line. */
const CodeIndex = styled(MsIndex)`
    text-align: center;
    padding-top: 36px;
`;

const RoomCodeEyebrow = styled(MsEyebrow)`
    margin-bottom: 6px;
    margin-top: 4px;
`;

const RoomCodeDisplay = styled(MsDisplay)`
    font-size: 84px;
    line-height: 1;
    letter-spacing: -0.04em;
`;

/* Small serif-italic caption showing the shareable URL. */
const UrlCaption = styled.div`
    margin-top: 12px;
    font-family: ${fonts.serif};
    font-style: italic;
    color: ${paper.inkSoft};
    font-size: 13px;
`;

/* Same caption inside the QR modal — slightly more headroom. */
const QrUrlCaption = styled.div`
    margin-top: 14px;
    font-family: ${fonts.serif};
    font-style: italic;
    font-size: 13px;
    color: ${paper.inkSoft};
`;

const ShareRow = styled(Row)`
    margin-top: 16px;
`;

const SettingsRow = styled(Row)`
    margin-top: 14px;
`;

/* Serif cog glyph inside the "Game settings" button. */
const CogGlyph = styled.span`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-style: italic;
`;

const PlayersEyebrow = styled(MsEyebrow)`
    padding: 8px 4px 0;
`;

const YouEyebrow = styled(MsEyebrow)`
    color: ${paper.sticky};
`;

const OfflineEyebrow = styled(MsEyebrow)`
    color: ${paper.red};
`;


const SpectatorRow = styled(MsRow)`
    opacity: 0.85;
`;

const SpectatorRule = styled(MsRule)`
    margin-top: 10px;
    opacity: 0.4;
`;

const ScanEyebrow = styled(MsEyebrow)`
    margin-bottom: 4px;
`;

const QrCodeDisplay = styled(MsDisplay)`
    font-size: 44px;
    line-height: 1;
    letter-spacing: -0.03em;
    margin-bottom: 14px;
`;

const CloseBtn = styled(MsBtn)`
    margin-top: 18px;
`;
import PaperRoomSettings, { type RoomSettings } from "./PaperRoomSettings";
import PaperGameMode from "./PaperGameMode";
import PaperPlayerManage from "./PaperPlayerManage";
import { ModalDialog } from "./Dialog";

const QRCard = styled(motion.create(MsPaper))`
    width: min(360px, calc(100vw - 32px));
    padding: 30px 26px 26px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const QRFrame = styled.div`
    position: relative;
    padding: 14px;
    background: ${paper.paper};
    box-shadow:
        inset 0 0 0 1px ${shade(0.12)},
        inset 0 0 0 6px ${paper.paper},
        inset 0 0 0 7px ${shade(0.18)};

    /* corner crop marks — like a printer's registration ticks */
    &::before, &::after {
        content: '';
        position: absolute;
        width: 10px;
        height: 10px;
        border: 1.5px solid ${paper.red};
        opacity: 0.7;
    }
    &::before { top: -5px; left: -5px; border-right: 0; border-bottom: 0; }
    &::after  { bottom: -5px; right: -5px; border-left: 0; border-top: 0; }

    & > svg {
        display: block;
        color: ${paper.ink};
    }
`;

interface Props {
    view: WaitingRoomView;
    roomId: string;
    players: PlayerInfo[];
    common: CommonRoomData;
    /** Who's looking. Passed in rather than dug out of `common`: room data is
     *  the same for everyone in the room, including the TV, which has no self. */
    self: PlayerInfo;
}

type Sheet =
    | { type: "rules" }
    | { type: "mode" }
    | { type: "roster"; playerId?: string }
    | null;

export default function WaitingRoom({ view, roomId, players, common, self }: Props) {
    const { sendMessage } = useWebSocket();
    const { isHost, minPlayersMet, settings: initialSettings } = view;
    const [qrOpen, setQrOpen] = useState(false);
    const [sheet, setSheet] = useState<Sheet>(null);

    // Canonical shareable link (QR + share sheet point here), not the host's
    // current page. e.g. https://misquote.party/join/ABCD
    const inviteUrl = joinUrl(window.location.origin, roomId);

    const [localSettings, setLocalSettings] = useState<RoomSettings>({
        ...initialSettings,
        totalRounds: common?.totalRounds ?? null,
    });

    const handleStart = () => {
        sendMessage({
            type: "start_game",
            ...localSettings,
            timerDurationSeconds: localSettings.timerDurationSeconds as 30 | 60 | 120 | 180 | 300,
        });
    };

    const handleShare = async () => {
        const shareData = {
            title: "Join Misquote",
            text: `Join my game with code: ${roomId}`,
            url: inviteUrl,
        };
        if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
            try { await navigator.share(shareData); } catch { /* user cancelled */ }
        } else {
            navigator.clipboard.writeText(inviteUrl);
        }
    };

    const activePlayers = players.filter(p => (p.role || "player") === "player");
    const spectators = players.filter(p => p.role === "spectator");

    if (sheet?.type === "rules") {
        return (
            <MsSheet>
                <PaperRoomSettings
                    settings={localSettings}
                    onChange={setLocalSettings}
                    ctaLabel="Done"
                    onConfirm={() => setSheet(null)}
                    onBack={() => setSheet(null)}
                    readOnly={!isHost}
                />
            </MsSheet>
        );
    }

    if (sheet?.type === "mode") {
        return (
            <MsSheet>
                <PaperGameMode
                    value={localSettings.promptMode}
                    onChange={mode => setLocalSettings({ ...localSettings, promptMode: mode })}
                    onConfirm={() => setSheet(null)}
                    onBack={() => setSheet(null)}
                    readOnly={!isHost}
                />
            </MsSheet>
        );
    }

    if (sheet?.type === "roster" && isHost) {
        return (
            <MsSheet>
                <PaperPlayerManage
                    players={players}
                    selfId={self.id}
                    isHost={isHost}
                    onClose={() => setSheet(null)}
                    initialPlayerId={sheet.playerId}
                />
            </MsSheet>
        );
    }

    return (
        <MsScreen>
            <MsBody wide>
                <MsSplitGrid gap={16} align="start" padTop={8}>
                    {/* Left: room code + share */}
                    <div>
                        <CodeIndex drop r1="-1.5deg">
                            <MsTape placement="tl" />
                            <RoomCodeEyebrow>Room code</RoomCodeEyebrow>
                            <RoomCodeDisplay>
                                <MaskHeadline>{roomId}</MaskHeadline>
                            </RoomCodeDisplay>
                            <UrlCaption>
                                misquote.party/join/{roomId.toLowerCase()}
                            </UrlCaption>
                            <ShareRow gap={8} justify="center">
                                <MsIconBtn haptic="select" onClick={handleShare}><ShareNetwork size={16} weight="bold" /> Share link</MsIconBtn>
                                <MsIconBtn onClick={() => setQrOpen(true)}><QrCode size={16} weight="bold" /> QR code</MsIconBtn>
                                {/* Renders nothing where casting can't work — no
                                    receiver configured, no Cast API (any iPhone),
                                    or no Chromecast on the network. */}
                                <CastButton roomCode={roomId} />
                            </ShareRow>
                        </CodeIndex>

                        {/* Settings button is host-only — non-hosts can't change
                            settings so the entry point is hidden for them. */}
                        {isHost && (
                            <SettingsRow gap={8} wrap>
                                <MsIconBtn onClick={() => setSheet({ type: "rules" })}>
                                    <CogGlyph>⚙</CogGlyph>
                                    Game settings
                                </MsIconBtn>
                                {/* The mode is its own entry point rather than a row
                                    inside settings: it changes what a round *is*,
                                    and it's the first thing a deckless table needs
                                    to find. The label carries the current choice so
                                    that's answerable without opening it. */}
                                <MsIconBtn onClick={() => setSheet({ type: "mode" })}>
                                    <Cards size={16} weight="bold" />
                                    {localSettings.promptMode === "digital" ? "No deck tonight" : "With the deck"}
                                </MsIconBtn>
                            </SettingsRow>
                        )}
                    </div>

                    {/* Right: players list */}
                    <Stack gap={8}>
                        <PlayersEyebrow>Players · {activePlayers.length}</PlayersEyebrow>

                        {activePlayers.map((p, i) => {
                            const you = p.id === self.id;
                            const initials = p.name.slice(0, 2);
                            const row = (
                                <MsRow you={you} style={{ animationDelay: `${0.15 + i * 0.07}s` }}>
                                    <MsAvatar you={you}>{initials}</MsAvatar>
                                    <MsName>
                                        {p.isHost && <MsNameMark you={you}>★</MsNameMark>}
                                        {p.name}
                                    </MsName>
                                    {you && <YouEyebrow>you</YouEyebrow>}
                                    {p.isConnected === false && (
                                        <OfflineEyebrow>offline</OfflineEyebrow>
                                    )}
                                </MsRow>
                            );
                            return isHost ? (
                                <MsPlayerButton key={p.id} onClick={() => setSheet({ type: "roster", playerId: p.id })}>
                                    {row}
                                </MsPlayerButton>
                            ) : (
                                <div key={p.id}>{row}</div>
                            );
                        })}

                        {spectators.length > 0 && (
                            <>
                                <SpectatorRule />
                                <MsEyebrow tone="mute">Spectators · {spectators.length}</MsEyebrow>
                                {spectators.map((p) => {
                                    const you = p.id === self.id;
                                    return (
                                        <SpectatorRow key={p.id} you={you}>
                                            <MsAvatar you={you}>{p.name.slice(0, 2)}</MsAvatar>
                                            <MsName>{p.name}</MsName>
                                            <MsEyebrow tone="mute">spectator</MsEyebrow>
                                        </SpectatorRow>
                                    );
                                })}
                            </>
                        )}
                    </Stack>
                </MsSplitGrid>
            </MsBody>
            <MsFooter wide>
                {isHost ? (
                    <MsBtn variant="red" disabled={!minPlayersMet} onClick={handleStart}>
                        {minPlayersMet ? "Start the round" : "Waiting for players…"}
                    </MsBtn>
                ) : (
                    <MsWaitingFor name={getHostName(players)} />
                )}
            </MsFooter>

            <AnimatePresence>
                {qrOpen && (
                    <ModalDialog
                        key="qr"
                        onClose={() => setQrOpen(false)}
                        aria-label={`Room code ${roomId} — scan the QR code to join`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                    >
                        <QRCard
                            deckle
                            initial={{ opacity: 0, y: 24, rotate: 0, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, rotate: -1.4, scale: 1 }}
                            exit={{ opacity: 0, y: 16, rotate: 0, scale: 0.96 }}
                            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <MsTape placement="tl" red />
                            <ScanEyebrow tone="red">Scan to join</ScanEyebrow>
                            {/* Plain text, not MaskHeadline: the code rides in as one
                                solid unit with the card. A self-clipping mask reveal here
                                fights the card's simultaneous y/scale/rotate and its 0.7s
                                slide outlasts the 0.32s entrance — that's the jank. */}
                            <QrCodeDisplay>
                                {roomId}
                            </QrCodeDisplay>
                            <QRFrame>
                                <QRCode
                                    value={inviteUrl}
                                    size={216}
                                    bgColor={paper.paper}
                                    fgColor={paper.ink}
                                />
                            </QRFrame>
                            <QrUrlCaption>
                                misquote.party/join/{roomId.toLowerCase()}
                            </QrUrlCaption>
                            <CloseBtn variant="ghost" onClick={() => setQrOpen(false)}>Close</CloseBtn>
                        </QRCard>
                    </ModalDialog>
                )}
            </AnimatePresence>
        </MsScreen>
    );
}
